import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Grid, TextField, Button, MenuItem, Box,
  IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Autocomplete, Stack, Pagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;
const EMPLOYEES_PER_PAGE = 10;

function CreateEmployee() {
  const [employee, setEmployee] = useState({
    id: null,
    type: '',
    user_id: '',
    employee_code: '',
    field: '',
  });

  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    axios.get(`${API_URL}/api/user-roles/`)
      .then(res => setTypes(res.data))
      .catch(err => console.error('Failed to fetch types:', err));
  }, []);

  useEffect(() => {
    if (employee.type) {
      axios.get(`${API_URL}/api/users-for-coupon-creation/?type=${employee.type}`)
        .then(res => setUsers(res.data))
        .catch(err => console.error('Failed to fetch users:', err));
    } else {
      setUsers([]);
    }
  }, [employee.type]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees/`);
      const employeesWithNames = await Promise.all(
        res.data.map(async (emp) => {
          try {
            const userRes = await axios.post(`${API_URL}/api/user/`, {
              userId: emp.user_id || emp.id
            });

            return {
              ...emp,
              name: userRes.data.name,
              surname: userRes.data.surname
            };
          } catch (error) {
            console.warn('Failed to fetch user info for member:', emp.user_id || emp.id);
            return emp;
          }
        })
      );
      setEmployees(employeesWithNames);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'type') {
      setEmployee(prev => ({ ...prev, user_id: '' }));
    }
  };

  const resetForm = () => {
    setEmployee({ id: null, type: '', user_id: '', employee_code: '', field: '' });
    setIsEditing(false);
  };

  const doSubmit = () => {
    const { id, user_id, employee_code, field, type } = employee;

    if (!user_id || !employee_code || !field || !type) {
      alert("Please fill in all required fields.");
      setConfirmOpen(false);
      return;
    }

    const payload = { user_id, employee_code, field, type };

    if (isEditing && id) {
      axios.put(`${API_URL}/api/employees/update/`, { ...payload, user_id: id })
        .then(() => {
          alert("Member updated!");
          fetchEmployees();
          resetForm();
        })
        .catch(err => {
          console.error(err);
          alert("Failed to update member");
        })
        .finally(() => setConfirmOpen(false));
    } else {
      axios.post(`${API_URL}/api/employees/create/`, payload)
        .then(() => {
          alert("Member created!");
          fetchEmployees();
          resetForm();
        })
        .catch(err => {
          console.error(err);
          alert("Failed to create member");
        })
        .finally(() => setConfirmOpen(false));
    }
  };

  const handleSubmit = () => setConfirmOpen(true);

  const handleEdit = (emp) => {
    setEmployee({
      id: emp.user_id || emp.id,
      user_id: emp.user_id || emp.id,
      employee_code: emp.employee_code,
      field: emp.field,
      type: emp.type || emp.role || '',
    });
    setIsEditing(true);
  };

  const handleDelete = (emp) => {
    if (window.confirm("Are you sure you want to delete this member?")) {
      axios.delete(`${API_URL}/api/employees/delete/`, { data: { user_id: emp.user_id || emp.id } })
        .then(() => {
          alert("Member deleted");
          fetchEmployees();
          if (emp.user_id === employee.id) resetForm();
        })
        .catch(err => {
          console.error(err);
          alert("Failed to delete member");
        });
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const q = searchQuery.trim().toLowerCase();
    if (q === '') return true;
    const codeMatch = emp.employee_code?.toLowerCase().includes(q);
    const fieldMatch = emp.field?.toLowerCase().includes(q);
    const nameMatch = ((emp.name || '') + ' ' + (emp.surname || '')).toLowerCase().includes(q);
    return codeMatch || fieldMatch || nameMatch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * EMPLOYEES_PER_PAGE,
    currentPage * EMPLOYEES_PER_PAGE
  );

  return (
    <div>
  <Header1 />
  
  <Typography
    variant="h3"
    align="center"
    fontWeight="bold"
    gutterBottom
    sx={{
      backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      letterSpacing: 3,
      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
      mb: 4,
    }}
  >
    Create Member
  </Typography>

  <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
    {/* Form */}
    <Card sx={{ p: 4, backgroundColor: '#1e1e1e', mb: 4 }}>
      <Typography variant="h5" sx={{ color: '#fff', mb: 3 }}>
        {isEditing ? 'Edit Member' : 'Create New Member'}
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Type */}
        <Box>
          <TextField
            select
            name="type"
            label="Type (Role)"
            fullWidth
            value={employee.type}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#ccc' } }}
            InputProps={{ style: { color: '#fff', backgroundColor: '#2b2b2b' } }}
          >
            {types.map((typeOption, idx) => (
              <MenuItem key={idx} value={typeOption}>
                {typeOption}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* User Autocomplete */}
        <Box>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => `${option.name || ''} ${option.surname || ''}`.trim()}
            value={users.find(u => u.id === employee.user_id) || null}
            onChange={(event, newValue) => setEmployee(prev => ({
              ...prev,
              user_id: newValue ? newValue.id : '',
            }))}
            disabled={!employee.type}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User"
                InputLabelProps={{ style: { color: '#ccc' } }}
                InputProps={{ ...params.InputProps, style: { color: '#fff', backgroundColor: '#2b2b2b' } }}
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            clearOnEscape
          />
        </Box>

        {/* Member Code */}
        <Box>
          <TextField
            name="employee_code"
            label="Member Code"
            fullWidth
            value={employee.employee_code}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#ccc' } }}
            InputProps={{ style: { color: '#fff', backgroundColor: '#2b2b2b' } }}
          />
        </Box>

        {/* Field */}
        <Box>
          <TextField
            name="field"
            label="Field"
            fullWidth
            value={employee.field}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#ccc' } }}
            InputProps={{ style: { color: '#fff', backgroundColor: '#2b2b2b' } }}
          />
        </Box>

        {/* Submit Button */}
        <Box>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#1E90FF',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#0077cc' },
              py: 1.5,
              letterSpacing: 1.5,
            }}
          >
            {isEditing ? 'Update Member' : 'Create Member'}
          </Button>
        </Box>

        {/* Cancel Edit */}
        {isEditing && (
          <Box>
            <Button
              fullWidth
              variant="outlined"
              onClick={resetForm}
              sx={{ borderColor: '#fff', color: '#fff' }}
            >
              Cancel Edit
            </Button>
          </Box>
        )}
      </Box>
    </Card>

    {/* Search Box */}
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        placeholder="Search members by code, name or field..."
        value={searchQuery}
        onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1); // Reset page on new search
        }}
        variant="outlined"
        size="small"
        sx={{
          backgroundColor: '#1e1e1e',
          input: { color: '#fff' },
          fieldset: { borderColor: '#444' },
          '&:hover fieldset': { borderColor: '#888' },
          '&.Mui-focused fieldset': { borderColor: '#1E90FF' },
        }}
      />
    </Box>

    {/* Employee List */}
    <Card sx={{ p: 3, backgroundColor: '#2a2a2a' }}>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Existing Members
      </Typography>

      {paginatedEmployees.length === 0 ? (
        <Typography sx={{ color: '#ccc' }}>No members found.</Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          {paginatedEmployees.map(emp => (
            <Box
              key={emp.user_id || emp.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
              sx={{ backgroundColor: '#3a3a3a', borderRadius: 1 }}
            >
              <Box>
                <Typography sx={{ color: '#fff' }}><strong>Code:</strong> {emp.employee_code}</Typography>
                <Typography sx={{ color: '#ccc' }}><strong>Field:</strong> {emp.field}</Typography>
                {(emp.name || emp.surname) && (
                  <Typography sx={{ color: '#ccc' }}><strong>User:</strong> {emp.name} {emp.surname}</Typography>
                )}
              </Box>
              <Box>
                <IconButton onClick={() => handleEdit(emp)} sx={{ color: 'orange' }}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(emp)} sx={{ color: 'red' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {totalPages > 1 && (
        <Stack spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Card>

    {/* Confirmation Dialog */}
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>{isEditing ? 'Confirm Update' : 'Confirm Create'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: '#000' }}>
          {isEditing
            ? 'Are you sure you want to update this member?'
            : 'Are you sure you want to create a new member?'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
        <Button onClick={doSubmit} variant="contained" color="primary">
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>

  <Footer2 />
</div>

  );
}

export default CreateEmployee;
